import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Divider,
} from '@mui/material';
import { Add, Refresh, Delete, ArrowBack, AccessTime, Schedule } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ChipRow = ({ label, items, selected, onSelect, getLabel, getValue }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography fontSize={13} fontWeight={700} color="#1a237e" mb={1}>{label}</Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {items.map((item, i) => {
        const val = getValue ? getValue(item) : item;
        const lbl = getLabel ? getLabel(item) : item;
        const active = selected === val;
        return (
          <Box key={i} onClick={() => onSelect(val)}
            sx={{
              px: 2, py: 0.8, borderRadius: 5, cursor: 'pointer', userSelect: 'none',
              background: active ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#f5f6fa',
              color: active ? '#fff' : '#1a237e',
              border: `1.5px solid ${active ? '#1a237e' : '#e8eaf6'}`,
              fontWeight: 600, fontSize: 13,
              transition: 'all 0.15s ease',
              '&:hover': { background: active ? 'linear-gradient(135deg,#1a237e,#1565c0)' : '#e8eaf6' },
            }}>
            {lbl}
          </Box>
        );
      })}
      {items.length === 0 && (
        <Typography fontSize={12} color="text.disabled" fontStyle="italic">No {label.toLowerCase()} available</Typography>
      )}
    </Box>
  </Box>
);

export default function Timetable() {
  const [view, setView] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [timetable, setTimetable] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selDay, setSelDay] = useState('');
  const [selPeriod, setSelPeriod] = useState('');
  const [selSubject, setSelSubject] = useState('');
  const [selTeacher, setSelTeacher] = useState('');
  const [selClassroom, setSelClassroom] = useState('');
  const [deletingPeriod, setDeletingPeriod] = useState(null);

  const handleDeletePeriod = async (id) => {
    if (!window.confirm('Delete this time period? This will fail if it is used in any timetable.')) return;
    setDeletingPeriod(id);
    const data = await InvokeApi(Config.main_url + apiList.deleteTimePeriod, { id });
    setDeletingPeriod(null);
    if (data?.code === '200') {
      const periodData = await InvokeApi(Config.main_url + apiList.getTimePeriods, { status: 'Active' });
      const sorted = (periodData?.periods || []).sort((a, b) => (a.period_order || 0) - (b.period_order || 0));
      setPeriods(sorted);
    } else {
      alert(data?.message || 'Cannot delete — period may be in use');
    }
  };
  const [pName, setPName] = useState('');
  const [pOrder, setPOrder] = useState('');
  const [pStart, setPStart] = useState('');
  const [pEnd, setPEnd] = useState('');
  const [pIsBreak, setPIsBreak] = useState(false);
  const [savingPeriod, setSavingPeriod] = useState(false);

  // Reload periods whenever addPeriod view opens
  useEffect(() => {
    if (view === 'addPeriod') {
      InvokeApi(Config.main_url + apiList.getTimePeriods, { status: 'Active' }).then(d => {
        setPeriods((d?.periods || []).sort((a, b) => (a.period_order || 0) - (b.period_order || 0)));
      });
    }
  }, [view]);

  const load = useCallback(async (cls) => {
    if (!cls) return;
    setLoading(true);
    const [ttData, subData, periodData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getTimetableByStdSec, { standard: cls.standard, section: cls.section, status: 'Active' }),
      InvokeApi(Config.main_url + apiList.getSubjects, { standard: cls.standard, section: cls.section }),
      InvokeApi(Config.main_url + apiList.getTimePeriods, { status: 'Active' }),
    ]);
    setTimetable(ttData?.timetable || []);
    setSubjects(subData?.subjects || []);
    const sortedPeriods = (periodData?.periods || []).sort((a, b) => (a.period_order || 0) - (b.period_order || 0));
    setPeriods(sortedPeriods);
    setLoading(false);
  }, []);

  const handleSelect = (cls) => {
    setSelected(cls);
    setView('timetable');
    load(cls);
  };

  const resetAddForm = () => {
    setSelDay(''); setSelPeriod(''); setSelSubject(''); setSelTeacher(''); setSelClassroom('');
  };

  const handleSaveTimetable = async () => {
    if (!selDay || !selPeriod || !selSubject) {
      alert('Day, Period and Subject are required'); return;
    }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addTimetable, {
      standard: selected.standard,
      section: selected.section,
      day_of_week: selDay,
      period_id: selPeriod,
      subject: selSubject,
      teacher: selTeacher || null,
      classroom: selClassroom || null,
      created_by: '1',
    });
    setSaving(false);
    if (data?.code === '200') {
      resetAddForm();
      setView('timetable');
      load(selected);
    } else alert(data?.message || 'Failed to add timetable entry');
  };

  const handleSavePeriod = async () => {
    if (!pName || !pStart || !pEnd || !pOrder) {
      alert('All period fields are required'); return;
    }
    setSavingPeriod(true);
    const data = await InvokeApi(Config.main_url + apiList.addTimePeriod, {
      period_name: pName,
      start_time: pStart,
      end_time: pEnd,
      period_order: pOrder,
      is_break: pIsBreak ? 1 : 0,
    });
    setSavingPeriod(false);
    if (data?.code === '200') {
      setPName(''); setPOrder(''); setPStart(''); setPEnd(''); setPIsBreak(false);
      // Reload periods
      const periodData = await InvokeApi(Config.main_url + apiList.getTimePeriods, { status: 'Active' });
      const sorted = (periodData?.periods || []).sort((a, b) => (a.period_order || 0) - (b.period_order || 0));
      setPeriods(sorted);
      setView('add');
    } else alert(data?.message || 'Failed to add period');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this timetable entry?')) return;
    await InvokeApi(Config.main_url + apiList.deleteTimetable, { id });
    load(selected);
  };

  // All periods sorted by order — break periods always included
  const allPeriodsSorted = [...periods].sort((a, b) => (a.period_order || 0) - (b.period_order || 0));

  // Only non-break periods that have at least one timetable entry get a column
  // Break periods always show regardless
  const visiblePeriods = allPeriodsSorted.filter(p => {
    if (p.is_break == 1) return true;   // always show break columns
    const pid = p.id || p.ID;
    return timetable.some(t => t.period_id == pid);  // only show if has data
  });

  // Non-break visible periods (for add-entry chip selector)
  const columnPeriods = visiblePeriods.filter(p => p.is_break != 1);

  const byDay = DAYS.reduce((acc, day) => {
    acc[day] = (timetable.filter(t => t.day_of_week === day))
      .sort((a, b) => (a.period_order || a.period_id || 0) - (b.period_order || b.period_id || 0));
    return acc;
  }, {});

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Timetable</Typography>
          <Typography variant="body2" color="text.secondary">Select a class to view or manage timetable</Typography>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  if (view === 'addPeriod') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => setView('add')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Add Time Period</Typography>
            <Typography variant="body2" color="text.secondary">Define a new period slot (e.g. Period 1: 8:00 - 8:45)</Typography>
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: 480 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField fullWidth size="small" label="Period Name *" placeholder="e.g. Period 1, Assembly"
                value={pName} onChange={e => setPName(e.target.value)} />
              <TextField fullWidth size="small" label="Period Order *" type="number" placeholder="e.g. 1"
                value={pOrder} onChange={e => setPOrder(e.target.value)} inputProps={{ min: 1 }} />
              <TextField fullWidth size="small" label="Start Time *" type="time"
                value={pStart} onChange={e => setPStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="e.g. 08:00" />
              <TextField fullWidth size="small" label="End Time *" type="time"
                value={pEnd} onChange={e => setPEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="e.g. 08:45" />
              {/* Break/Lunch toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                borderRadius: 2, border: `1.5px solid ${pIsBreak ? '#e65100' : '#e8eaf6'}`,
                background: pIsBreak ? '#fff3e0' : '#fafafa', cursor: 'pointer' }}
                onClick={() => setPIsBreak(v => !v)}>
                <Box sx={{ width: 20, height: 20, borderRadius: 1, border: `2px solid ${pIsBreak ? '#e65100' : '#bbb'}`,
                  background: pIsBreak ? '#e65100' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {pIsBreak && <Box sx={{ width: 10, height: 10, borderRadius: 0.5, background: '#fff' }} />}
                </Box>
                <Box>
                  <Typography fontSize={13} fontWeight={700} color={pIsBreak ? '#e65100' : 'text.primary'}>
                    Break / Lunch Period
                  </Typography>
                  <Typography fontSize={11} color="text.secondary">
                    This period spans all day columns — no subject is assigned
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={() => setView('add')} sx={{ borderRadius: 2, flex: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSavePeriod} disabled={savingPeriod}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, flex: 1 }}>
                {savingPeriod ? <CircularProgress size={20} color="inherit" /> : 'Add Period'}
              </Button>
            </Box>

            {/* Existing periods list */}
            {periods.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }}><Typography fontSize={12} color="text.secondary">Existing Periods</Typography></Divider>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {periods.map(p => (
                    <Box key={p.id || p.ID} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1.5, borderRadius: 2, background: '#f5f6fa', border: '1px solid #e8eaf6',
                    }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={700} color="#1a237e">{p.period_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">
                          {p.start_time_12hr || p.start_time} – {p.end_time_12hr || p.end_time} · Order: {p.period_order}
                        </Typography>
                      </Box>
                      <Tooltip title="Delete Period">
                        <IconButton size="small"
                          onClick={() => handleDeletePeriod(p.id || p.ID)}
                          disabled={deletingPeriod === (p.id || p.ID)}
                          sx={{ color: '#c62828', '&:hover': { background: '#ffebee' } }}>
                          {deletingPeriod === (p.id || p.ID)
                            ? <CircularProgress size={16} color="inherit" />
                            : <Delete fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (view === 'add') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => { resetAddForm(); setView('timetable'); }} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Add Timetable Entry</Typography>
            <Typography variant="body2" color="text.secondary">{selected?.standard} - {selected?.section}</Typography>
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: 600 }}>
          <CardContent sx={{ p: 3 }}>

            <ChipRow
              label="Select Day *"
              items={DAYS}
              selected={selDay}
              onSelect={setSelDay}
            />

            <ChipRow
              label="Select Period *"
              items={allPeriodsSorted.filter(p => p.is_break != 1)}
              selected={selPeriod}
              onSelect={setSelPeriod}
              getValue={p => p.id || p.ID}
              getLabel={p => (
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ fontWeight: 700 }}>{p.period_name}</Box>
                  <Box sx={{ fontSize: 11, opacity: 0.8 }}>{p.start_time_12hr || p.start_time}</Box>
                </Box>
              )}
            />

            {periods.length === 0 && (
              <Box sx={{ mb: 2, p: 1.5, background: '#fff3e0', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule sx={{ color: '#e65100', fontSize: 18 }} />
                <Typography fontSize={12} color="#e65100" fontWeight={600}>
                  No periods defined yet.{' '}
                  <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setView('addPeriod')}>
                    Add a period first
                  </Box>
                </Typography>
              </Box>
            )}

            <ChipRow
              label="Select Subject *"
              items={subjects}
              selected={selSubject}
              onSelect={setSelSubject}
              getValue={s => s.name}
              getLabel={s => s.name}
            />

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField size="small" label="Teacher (Optional)" value={selTeacher}
                onChange={e => setSelTeacher(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
              <TextField size="small" label="Classroom (Optional)" value={selClassroom}
                onChange={e => setSelClassroom(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button variant="outlined" onClick={() => { resetAddForm(); setView('timetable'); }}
                sx={{ borderRadius: 2, flex: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveTimetable} disabled={saving}
                sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, flex: 1 }}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add to Timetable'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Button fullWidth variant="outlined" startIcon={<AccessTime />} onClick={() => setView('addPeriod')}
              sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 600 }}>
              Manage Time Periods
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => setView('grid')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Timetable</Typography>
            <Typography variant="body2" color="text.secondary">
              {selected?.standard} - {selected?.section} · {timetable.length} entries
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => load(selected)} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setView('add')}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Entry
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : periods.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Schedule sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
            <Typography color="text.secondary" fontWeight={500}>No time periods defined</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Add periods first before creating a timetable
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setView('addPeriod')}
              sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
              Add Time Period
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)' }}>
                  {/* Day column */}
                  <TableCell sx={{ fontWeight: 700, color: '#fff', fontSize: 13, minWidth: 100,
                    borderRight: '1px solid rgba(255,255,255,0.2)', position: 'sticky', left: 0,
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)', zIndex: 2 }}>
                    Day
                  </TableCell>
                  {/* All visible periods in order — break gets orange, regular gets normal */}
                  {visiblePeriods.map(p => {
                    const isBreak = p.is_break == 1;
                    return (
                      <TableCell key={p.id || p.ID} align="center"
                        sx={{ fontWeight: 700, color: '#fff', fontSize: 12,
                          minWidth: isBreak ? 90 : 120,
                          borderRight: '1px solid rgba(255,255,255,0.2)',
                          background: isBreak ? 'linear-gradient(135deg,#e65100,#ff8f00)' : 'transparent',
                        }}>
                        <Box fontWeight={700}>{p.period_name}</Box>
                        <Box sx={{ fontSize: 9, opacity: 0.85, fontWeight: 400 }}>
                          {`${p.start_time_12hr || p.start_time} – ${p.end_time_12hr || p.end_time}`}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {DAYS.map((day, di) => (
                  <TableRow key={day} sx={{ background: di % 2 === 0 ? '#fff' : '#fafbff' }}>
                    {/* Day label */}
                    <TableCell sx={{ fontWeight: 700, fontSize: 13, color: '#1a237e',
                      background: '#f5f6fa', borderRight: '1px solid #e8eaf6',
                      whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 1 }}>
                      {day}
                    </TableCell>
                    {/* All visible periods in order */}
                    {visiblePeriods.map(p => {
                      const pid = p.id || p.ID;
                      const isBreak = p.is_break == 1;

                      if (isBreak) {
                        return (
                          <TableCell key={`${day}-${pid}`} align="center"
                            sx={{ p: 0, background: '#fff8f0', borderRight: '1px solid #ffe0b2' }}>
                            <Box sx={{ height: '100%', minHeight: 48,
                              background: 'linear-gradient(180deg,#ffe0b2,#fff3e0)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography fontSize={11} fontWeight={700} color="#e65100"
                                sx={{ writingMode: 'horizontal-tb', textAlign: 'center', px: 0.5 }}>
                                {p.period_name}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      }

                      const entry = timetable.find(t => t.day_of_week === day && t.period_id == pid);
                      return (
                        <TableCell key={`${day}-${pid}`} align="center"
                          sx={{ p: 1, borderRight: '1px solid #f0f0f0', verticalAlign: 'middle' }}>
                          {entry ? (
                            <Box sx={{ position: 'relative', px: 0.5 }}>
                              <Box sx={{ p: 1, borderRadius: 1.5, background: '#e8eaf6', border: '1px solid #c5cae9' }}>
                                <Typography fontSize={12} fontWeight={700} color="#1a237e">{entry.subject}</Typography>
                                {entry.teacher && <Typography fontSize={10} color="text.secondary">{entry.teacher}</Typography>}
                                {entry.classroom && <Typography fontSize={10} color="text.disabled">Room: {entry.classroom}</Typography>}
                              </Box>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDelete(entry.id || entry.ID)}
                                  sx={{ position: 'absolute', top: -6, right: -6, color: '#c62828',
                                    background: '#fff', border: '1px solid #ffcdd2', p: 0.2, width: 18, height: 18,
                                    '&:hover': { background: '#ffebee' } }}>
                                  <Delete sx={{ fontSize: 11 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ minHeight: 48 }} />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
